// THE ONE TRANSPORT SEAM. Every screen in this client talks to `Transport` and to nothing else.
//
// `Transport` is not a hand-written interface that happens to resemble the contract — it IS the
// generated client's type. `createV2Client` in `contract/v2-client/index.ts` is rendered from
// `functions/src/v2/contract/schema.ts`, so a screen that compiles against `Transport` compiles
// against the schema, and a schema change that a screen cannot survive is a typecheck failure here
// rather than a runtime surprise in front of an owner.
//
// TWO IMPLEMENTATIONS, and only two:
//   • `FakeTransport`  (transport/fake/) — contract-typed fixtures covering every arm the schema
//     allows. This is what `npm run dev` and every test run against.
//   • `HttpTransport`  (transport/http.ts) — the real one, built by `createV2Client` from a config
//     object this batch never fills in. No base URL, no project id and no key exists in this repo.
//
// A third implementation is a design smell: it would mean a screen needed something the contract
// cannot say, and that is W2.x's to fix in the schema, never this client's to patch around.

import type { V2Client } from "@contract";

/** The seam. Structurally identical to the generated client, by construction. */
export type Transport = V2Client;

export type {
  AppealSnapshot,
  AttestationCopy,
  CanRaise,
  CompView,
  ContractStamp,
  Copy,
  CreditsGetResponse,
  CreditsSpendResponse,
  DecisionNotRun,
  DegradedField,
  EvaluateCaseResponse,
  FetchLetterResponse,
  FilingDeadline,
  GuardId,
  Jurisdiction,
  LegalDocRef,
  LetterJobView,
  Money,
  OutcomeResult,
  OutcomesResponse,
  PerAppealCaps,
  PinnedCopy,
  PropertyTypeCode,
  ProtestWindow,
  QueueLetterResponse,
  Ratio,
  ResolvePropertyResponse,
  SearchCompsResponse,
  SubjectView,
  V2Error,
  V2ErrorCode,
  V2Failure,
  V2Result,
  ValueResponse,
} from "@contract";

/**
 * A guard decision as the contract shapes it, narrowed to the parts every screen reads the same way.
 *
 * The generated file inlines one union per guard (so each carries its own `value` type). Screens that
 * only need "did it run, what did it decide, what sentence goes with it" read through this; screens
 * that need a guard's typed `value` read the generated type directly and keep the compiler's help.
 */
export type AnyDecision =
  | {
      ran: true;
      outcome: "pass" | "info" | "warn" | "block";
      value: unknown;
      evaluatedOver: { [key: string]: number };
      reason?: string;
      copy: import("@contract").Copy | null;
    }
  | ({ ran: false } & import("@contract").DecisionNotRun);

/** True when a decision ran and came back blocking. The one place "is this refused" is decided. */
export function isBlocked(decision: AnyDecision | undefined): boolean {
  return decision !== undefined && decision.ran && decision.outcome === "block";
}

/** True when a decision ran and passed. ABSENCE IS NEVER A PASS — a decision that never ran is unknown. */
export function isPass(decision: AnyDecision | undefined): boolean {
  return decision !== undefined && decision.ran && decision.outcome === "pass";
}
